// src/pages/Register.js
import React, { useState } from "react";
import { Player } from "@lottiefiles/react-lottie-player";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebaseConfig";
import Lineanimation from "../animations/SignUp.json";
import { motion } from "framer-motion";
import toast from "react-hot-toast";

export default function Register() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const countries = [
    "Pakistan", "India", "United States", "United Kingdom", "Canada",
    "Australia", "Germany", "France", "Saudi Arabia", "UAE"
  ];

  const showError = (msg) => {
    setError(msg);
    toast.error(msg);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");

    if (!phone.startsWith("+")) {
      return showError("Phone number must include a country code (e.g. +92 3484485412)");
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return showError("Please enter a valid email address!");
    }

    if (password.length < 8) {
      return showError("Password must be at least 8 characters long!");
    }

    if (password !== confirmPassword) {
      return showError("Passwords do not match!");
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Save profile in Firestore (users collection, doc id = uid)
      const userRef = doc(db, "users", user.uid);
      await setDoc(userRef, {
        firstName,
        lastName,
        phone,
        country,
        email,
      });

      navigate("/"); // Go to home page after signup
    } catch (err) {
      // Auth errors (from createUserWithEmailAndPassword)
      if (err.code === "auth/email-already-in-use") {
        showError("This email is already registered. Please log in instead.");
        return;
      }
      if (err.code === "auth/invalid-email") {
        showError("Please enter a valid email address.");
        return;
      }
      if (err.code === "auth/weak-password") {
        showError("Password is too weak. Use at least 8 characters.");
        return;
      }
      if (err.code === "auth/operation-not-allowed") {
        showError("Sign-up is not enabled. Contact support.");
        return;
      }
      // Firestore permission error (profile save failed after sign-up)
      if (err.code === "permission-denied" || (err.message && err.message.includes("permission"))) {
        showError("Account created but we couldn't save your profile. Please log in and complete your profile from Profile Setup.");
        return;
      }
      // Show other errors as-is (e.g. network)
      showError(err.message || "Something went wrong. Please try again.");
    }
  };

  return (
    <div className="flex flex-col md:flex-row items-center justify-center min-h-screen">
      {/* Left Side - Animation */}
      <motion.div
        className="flex-1 flex justify-center items-center p-6"
        initial={{ scale: 3.0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.5, ease: "easeOut" }}
      >
        <Player autoplay loop src={Lineanimation} style={{ height: "600px", width: "450px" }} />
      </motion.div>

      {/* Right Side - Form */}
      <div className="flex-1 flex justify-center items-center p-2 w-full">
        <form onSubmit={handleRegister} className="bg-white p-8 rounded-lg shadow-md w-full max-w-md space-y-4">
          <h2 className="text-2xl font-bold text-center">Create Account</h2>

          {error && <p className="text-red-500 text-sm text-center">{error}</p>}

          <input type="text" placeholder="First Name" className="w-full p-2 border rounded" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
          <input type="text" placeholder="Last Name" className="w-full p-2 border rounded" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
          <input type="text" placeholder="Phone Number with country code (e.g. +92 3484485412)" className="w-full p-2 border rounded" value={phone} onChange={(e) => setPhone(e.target.value)} required />

          <select className="w-full p-2 border rounded" value={country} onChange={(e) => setCountry(e.target.value)} required>
            <option value="">Select Country</option>
            {countries.map((c, i) => <option key={i} value={c}>{c}</option>)}
          </select>

          <input type="email" placeholder="Email" className="w-full p-2 border rounded" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <input type="password" placeholder="Password (min 8 chars)" className="w-full p-2 border rounded" value={password} onChange={(e) => setPassword(e.target.value)} required />
          <input type="password" placeholder="Re-enter Password" className="w-full p-2 border rounded" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />

          <button type="submit" className="w-full bg-brand text-black font-semibold py-2 rounded">Sign Up</button>
        </form>
      </div>
    </div>
  );
}
